import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SystemMetrics, Notification, Tenant, Certificate, Document, auth } from '../services/api';

interface SystemHealth {
  tenants: boolean;
  certificates: boolean;
  documents: boolean;
  sunat: boolean;
  auth: boolean;
  analytics: boolean;
  notifications: boolean;
}

interface ServiceStatus {
  [serviceName: string]: {
    status: 'healthy' | 'unhealthy' | 'unknown';
    version?: string;
    last_check?: string;
    response_time?: number;
  };
}

interface User {
  id: string;
  username: string;
  email?: string;
  role: string;
  tenant_id?: string;
}

interface AppState {
  // Auth state
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;

  // System state
  systemHealth: SystemHealth;
  serviceStatus: ServiceStatus;
  isWebSocketConnected: boolean;

  // Metrics
  currentMetrics: SystemMetrics | null;
  metricsHistory: SystemMetrics[];

  // Data
  tenants: Tenant[];
  certificates: Certificate[];
  documents: Document[];
  notifications: Notification[];

  // UI state
  activeTab: string;
  isDarkMode: boolean;
  sidebarCollapsed: boolean;
  isLoading: boolean;

  // Auth actions
  login: (token: string, user: User) => void;
  logout: () => void;
  setUser: (user: User) => void;
  checkAuth: () => void;

  // Actions
  setSystemHealth: (health: Partial<SystemHealth>) => void;
  setServiceStatus: (status: ServiceStatus) => void;
  setWebSocketConnected: (connected: boolean) => void;
  updateMetrics: (metrics: SystemMetrics) => void;
  addMetricsToHistory: (metrics: SystemMetrics) => void;
  setTenants: (tenants: Tenant[]) => void;
  setCertificates: (certificates: Certificate[]) => void;
  setDocuments: (documents: Document[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationAsRead: (id: string) => void;
  setActiveTab: (tab: string) => void;
  toggleDarkMode: () => void;
  toggleSidebar: () => void;
  setLoading: (loading: boolean) => void;

  // Computed
  getHealthyServicesCount: () => number;
  getUnreadNotifications: () => Notification[];
  getCriticalAlerts: () => Notification[];
}

export const useStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isAuthenticated: auth.isAuthenticated(),
      user: null,
      token: auth.getToken(),
      systemHealth: {
        tenants: false,
        certificates: false,
        documents: false,
        sunat: false,
        auth: false,
        analytics: false,
        notifications: false,
      },
      serviceStatus: {},
      isWebSocketConnected: false,
      currentMetrics: null,
      metricsHistory: [],
      tenants: [],
      certificates: [],
      documents: [],
      notifications: [],
      activeTab: 'dashboard',
      isDarkMode: false,
      sidebarCollapsed: false,
      isLoading: false,

      // Auth actions
      login: (token, user) => {
        auth.setToken(token);
        set({
          isAuthenticated: true,
          user,
          token
        }, false, 'login');
      },

      logout: () => {
        auth.removeToken();
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          tenants: [],
          certificates: [],
          documents: []
        }, false, 'logout');
      },

      setUser: (user) =>
        set({ user }, false, 'setUser'),

      checkAuth: () => {
        const isAuth = auth.isAuthenticated();
        const token = auth.getToken();

        let user = null;
        if (isAuth && token) {
          try {
            // Try to decode user info from token if no user is stored
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            user = {
              id: tokenPayload.sub,
              username: tokenPayload.username,
              email: tokenPayload.email || '',
              role: tokenPayload.role,
              tenant_id: tokenPayload.tenant_id
            };
          } catch (error) {
            console.error('Error decoding token:', error);
            // If token is invalid, remove it
            auth.removeToken();
            set({
              isAuthenticated: false,
              token: null,
              user: null
            }, false, 'checkAuth-invalid-token');
            return;
          }
        }

        set({
          isAuthenticated: isAuth,
          token: isAuth ? token : null,
          user: isAuth ? user : null
        }, false, 'checkAuth');
      },

      // Actions
      setSystemHealth: (health) =>
        set((state) => ({
          systemHealth: { ...state.systemHealth, ...health },
        }), false, 'setSystemHealth'),

      setServiceStatus: (status) =>
        set({ serviceStatus: status }, false, 'setServiceStatus'),

      setWebSocketConnected: (connected) =>
        set({ isWebSocketConnected: connected }, false, 'setWebSocketConnected'),

      updateMetrics: (metrics) =>
        set({ currentMetrics: metrics }, false, 'updateMetrics'),

      addMetricsToHistory: (metrics) =>
        set((state) => ({
          metricsHistory: [
            ...state.metricsHistory.slice(-99), // Keep last 100 entries
            metrics,
          ],
        }), false, 'addMetricsToHistory'),

      setTenants: (tenants) =>
        set({ tenants }, false, 'setTenants'),

      setCertificates: (certificates) =>
        set({ certificates }, false, 'setCertificates'),

      setDocuments: (documents) =>
        set({ documents }, false, 'setDocuments'),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [notification, ...state.notifications],
        }), false, 'addNotification'),

      markNotificationAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }), false, 'markNotificationAsRead'),

      setActiveTab: (tab) =>
        set({ activeTab: tab }, false, 'setActiveTab'),

      toggleDarkMode: () =>
        set((state) => ({ isDarkMode: !state.isDarkMode }), false, 'toggleDarkMode'),

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }), false, 'toggleSidebar'),

      setLoading: (loading) =>
        set({ isLoading: loading }, false, 'setLoading'),

      // Computed
      getHealthyServicesCount: () => {
        const health = get().systemHealth;
        return Object.values(health).filter(Boolean).length;
      },

      getUnreadNotifications: () => {
        return get().notifications.filter((n) => !n.read);
      },

      getCriticalAlerts: () => {
        return get().notifications.filter((n) =>
          n.type === 'error' && n.category === 'system' && !n.read
        );
      },
    }),
    {
      name: 'sunat-dashboard-store',
    }
  )
);

// Selectors
export const useAuth = () => useStore((state) => ({
  isAuthenticated: state.isAuthenticated,
  user: state.user,
  token: state.token
}));
export const useSystemHealth = () => useStore((state) => state.systemHealth);
export const useCurrentMetrics = () => useStore((state) => state.currentMetrics);
export const useNotifications = () => useStore((state) => state.notifications);
export const useActiveTab = () => useStore((state) => state.activeTab);
export const useWebSocketStatus = () => useStore((state) => state.isWebSocketConnected);